// eslint-disable-next-line no-restricted-imports -- This is the lock wrapper; it must import the raw function.
import { fetchAuthSession as amplifyFetchAuthSession } from "aws-amplify/auth";

const LOCK_NAME = "cognito-token-refresh";

/**
 * Cross-tab lock for Cognito token refresh using the Web Locks API.
 *
 * AWS Cognito uses refresh-token rotation: once a refresh token is consumed,
 * the old one is invalidated. Amplify v6 already deduplicates concurrent
 * `fetchAuthSession` calls **within a single tab** via `deDupeAsyncFunction`.
 * However, when multiple browser tabs trigger a refresh simultaneously, each
 * tab sends the same refresh token independently — the second tab receives a
 * `RefreshTokenReuseException`, which clears tokens and signs the user out.
 *
 * This module wraps `fetchAuthSession` with `navigator.locks.request` so that
 * only one tab can refresh at a time. While one tab holds the lock and
 * refreshes tokens, other tabs wait. When the waiting tab acquires the lock
 * the tokens in `localStorage` are already fresh, so Amplify reads them
 * without issuing another network refresh.
 *
 * Falls back to calling `fetchAuthSession` directly when the Web Locks API
 * is not available (e.g., non-secure contexts).
 */

type AuthSessionResult = Awaited<ReturnType<typeof amplifyFetchAuthSession>>;
type FetchAuthSessionOptions = Parameters<typeof amplifyFetchAuthSession>[0];

/**
 * Drop-in replacement for Amplify's `fetchAuthSession` that coordinates
 * token refreshes across browser tabs via the Web Locks API.
 *
 * Within a single tab, Amplify's built-in `deDupeAsyncFunction` already
 * prevents duplicate refresh requests. This wrapper adds the cross-tab
 * guarantee: only one tab holds the lock at a time, so concurrent tabs
 * never race with the same refresh token.
 */
export async function fetchAuthSessionWithLock(
  options?: FetchAuthSessionOptions
): Promise<AuthSessionResult> {
  if (typeof navigator === "undefined" || !("locks" in navigator)) {
    return amplifyFetchAuthSession(options);
  }

  return navigator.locks.request(LOCK_NAME, async () => {
    return amplifyFetchAuthSession(options);
  });
}
